import {
  StepIdentifierEnum,
  StepType,
} from "@redotech/redo-customer-portal-shared/hooks/useReturnConfirmation/use-steps";
import { Resource } from "@redotech/redo-model/localization/resource";
import CheckCircleFilled from "@redotech/redo-web/arbiter-icon/check-circle_filled.svg";
import PlaceHolder from "@redotech/redo-web/arbiter-icon/placeholder_filled.svg";
import { Flex } from "@redotech/redo-web/flex";
import { Icon } from "@redotech/redo-web/icon";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { ReactElement } from "react";
import { BlueYonderQRCard } from "./blue-yonder-qr-card";
import { InStoreLocationsButton } from "./in-store-locations";
import { InStorePackingSlipButton } from "./in-store-packing-slip-button";
import { LabelButtons } from "./label-buttons";
import { ReturnLocationsButton } from "./return-locations-button";

export function ReturnStepsCard({
  returnId,
  copyResource,
  shouldAutoOpenFedexLocations = false,
  steps,
}: {
  returnId: string;
  copyResource: Resource | undefined;
  shouldAutoOpenFedexLocations?: boolean;
  steps: StepType[] | undefined;
}) {
  function getStepComponent(step: StepType): ReactElement | null {
    switch (step.type) {
      case StepIdentifierEnum.DROP_OFF_PACKAGE:
        return <ReturnLocationsButton trackable={step.trackable} />;
      case StepIdentifierEnum.EXCHANGE_ORDER:
        return null;
      case StepIdentifierEnum.GREEN_RETURN_INSTRUCTIONS:
        return null;
      case StepIdentifierEnum.GREEN_RETURN_PROCESSED:
        return null;
      case StepIdentifierEnum.HAPPY_RETURNS_DROP_OFF:
        return null;
      case StepIdentifierEnum.HAPPY_RETURNS_PROCESSED:
        return null;
      case StepIdentifierEnum.BLUE_YONDER_DROP_OFF:
        return (
          <BlueYonderQRCard
            autoOpen={shouldAutoOpenFedexLocations}
            blueYonderQRCodes={step.blueYonderQRCodes}
            returnId={returnId}
          />
        );
      case StepIdentifierEnum.IN_STORE_PRINT_PACKING_SLIP:
        return (
          <InStorePackingSlipButton packingSlipLink={step.packingSlipLink} />
        );

      case StepIdentifierEnum.IN_STORE_PROCESSED:
        return null;
      case StepIdentifierEnum.IN_STORE_RETURN:
        return <InStoreLocationsButton returnId={returnId} />;
      case StepIdentifierEnum.MANUAL_REVIEW:
        return null;
      case StepIdentifierEnum.NONE:
        return null;
      case StepIdentifierEnum.PICKUP_ATTACH_LABEL:
        return null;
      case StepIdentifierEnum.PICKUP_PLACE_PACKAGE:
        return null;
      case StepIdentifierEnum.PICKUP_PRINT_LABEL:
        return step.shipmentInfo ? (
          <LabelButtons
            carrier={step.shipmentInfo.carrier}
            commercialInvoice={step.shipmentInfo.commercialInvoice}
            trackable={step.shipmentInfo.trackable}
          />
        ) : null;
      case StepIdentifierEnum.PROCESSED:
        return null;
      case StepIdentifierEnum.PROCESSED_REPAIR:
        return null; //TODO: TrackRepairButton
      case StepIdentifierEnum.SHIP_BACK_REPAIR:
        return null;
      case StepIdentifierEnum.SHIPMENT_TRANSIT:
        return step.shipmentInfo ? (
          <LabelButtons
            carrier={step.shipmentInfo.carrier}
            commercialInvoice={step.shipmentInfo.commercialInvoice}
            trackable={step.shipmentInfo.trackable}
          />
        ) : null;
      case StepIdentifierEnum.WARNING:
        return null;
      default:
        assertNever(step);
    }
  }
  return (
    <Flex flexDirection="column" gap="3xl" pb="sm" pt="sm">
      <Text fontSize="lg" fontWeight="semibold">
        {copyResource?.STATUS_STEPS_TITLE}
      </Text>
      <Flex flexDirection="column">
        {steps?.map((step: StepType, i) => {
          return (
            <Flex flexDirection="row" gap="sm" key={i}>
              <Flex align="center" flexDirection="column">
                <Flex flexDirection="column">
                  <Icon
                    arbiterIconSvg={(props) =>
                      step.complete
                        ? CheckCircleFilled(props)
                        : PlaceHolder(props)
                    }
                    color={step.complete ? "success" : "light"}
                    size="compact"
                  />
                </Flex>
                {i != steps.length - 1 && (
                  <Flex
                    flex={1}
                    flexDirection="column"
                    pb="xxs"
                    pt="xxs"
                    style={{
                      borderColor: "#E5E5E5",
                      borderWidth: 1,
                      borderRadius: 12,
                      borderStyle: "solid",
                      width: "2px",
                    }}
                  />
                )}
              </Flex>

              <Flex flex={1} flexDirection="column" pb="sm">
                <Text fontSize="md" fontWeight="medium">
                  {step.title}
                </Text>
                <Text fontSize="sm" fontWeight="regular">
                  {step.subtitle}
                </Text>
                {getStepComponent(step)}
              </Flex>
            </Flex>
          );
        })}
      </Flex>
    </Flex>
  );
}
